import { cellAtWorld } from '../grid.ts';
import { withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Hero } from '../types.ts';
import type { Pt } from '../caza-types.ts';
import type { LagoonRules, LagoonState, LagoonTrack } from '../isla-types.ts';
import { ev, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { fallHero, setOpen } from './isla-ice.ts';

/**
 * What walked under the ice (docs/sendas/barbaro.md, chapter 4, act 2, the puzzle: «Lo que caminó debajo del hielo»;
 * the ground in battle/isla-layout.ts). The lagoon's ice so clear the bottom shows: frozen under it, the tracks of all
 * that crossed it in seventy years. Sigrun's six (a boot with a tack in its left heel: a dot), the Wolf's over all of
 * hers, the pack's. Where two cross, the newer walks over (its prints whole, the other's cut): seen up close, at each
 * crossing. Her last track is the one none of hers walks over; the longest and clearest is of the first days, when she
 * still walked far. Broken where it ends, won (the bag is there). Broken elsewhere: the first time the ice cracks round
 * it; after that it gives way, the hero in the water, that part of the lagoon open and its crossings gone. Wolves off the
 * shore every half minute. Lost only with the hero down.
 *
 * Cost: a dozen crossings looked at once a tick against the hero; the ice's open parts the grid's plugs.
 */

const lr = (s: BattleState): LagoonRules | undefined => s.rules?.senda?.isla?.lagoon;
const ls = (s: BattleState): LagoonState | undefined => s.senda?.isla?.lagoon;

const fresh = (r: LagoonRules): LagoonState => ({
  seen: r.crossings.map(() => 0),
  ends: r.ends.map(() => 0),
  wrongs: 0,
  reading: -1,
  readAt: -1,
  goal: -1,
  t0: -1,
  next: r.wolves.first,
  falls: 0,
  done: -1,
});

/** Whether a crossing is one of hers with hers (the ones that order her days). */
export const lagoonHers = (r: LagoonRules, i: number): boolean => {
  const c = r.crossings[i];
  return !!c && (r.tracks[c.a] as LagoonTrack).who === 0 && (r.tracks[c.b] as LagoonTrack).who === 0;
};

/** The cells within `rad` of a point (the ice that gives way). */
const cellsNear = (s: BattleState, p: Pt, rad: number): number[] => {
  const out = new Set<number>();
  for (let dy = -rad; dy <= rad; dy += 433)
    for (let dx = -rad; dx <= rad; dx += 500) {
      if (dx * dx + dy * dy > rad * rad) continue;
      const c = cellAtWorld(s, p.x + dx, p.y + dy);
      if (c >= 0) out.add(c);
    }
  return [...out].sort((a, b) => a - b);
};

const sunkCells = (s: BattleState, r: LagoonRules, w: LagoonState): number[][] => w.ends.map((v, k) => (v === 2 ? cellsNear(s, r.ends[k] as Pt, r.sinkR) : []));

/** The hero's tap on an end (by letter): it walks there and breaks the ice. */
export const lagoonOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = lr(s);
  const w = ls(s);
  if (!r || !w || w.done >= 0 || at < 0 || at >= r.ends.length || w.ends[at] === 2) return;
  w.goal = at;
  w.t0 = -1;
  const p = r.ends[at] as Pt;
  orderMove(s, h, p.x, p.y);
};

const breakAt = (s: BattleState, r: LagoonRules, w: LagoonState, h: Hero, k: number, cue: (c: string) => void): void => {
  const p = r.ends[k] as Pt;
  w.goal = -1;
  w.t0 = -1;
  ev(s, { t: 'senda', kind: 'iBreak', npc: '', x: p.x, y: p.y, n: k });
  if (k === r.last) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'iFound', npc: '', x: p.x, y: p.y, n: k });
    cue('found');
    return;
  }
  w.wrongs++;
  // The first wrong only cracks it round; after that the ice gives way.
  if (w.wrongs === 1 && w.ends[k] === 0) {
    w.ends[k] = 1;
    ev(s, { t: 'senda', kind: 'iCrackRing', npc: '', x: p.x, y: p.y, n: k });
    cue('crack1');
    return;
  }
  w.ends[k] = 2;
  w.falls++;
  r.crossings.forEach((c, i) => {
    if (w.seen[i] !== 2 && withinRange(c.at.x, c.at.y, p.x, p.y, r.lostR)) w.seen[i] = 2;
  });
  ev(s, { t: 'senda', kind: 'iSink', npc: '', x: p.x, y: p.y, n: k });
  setOpen(s, sunkCells(s, r, w));
  fallHero(s, h, r.fallBp, 'iFall');
  // Out of the open water, the pack (the hole is theirs now).
  for (let i = 0; i < r.sinkWolves; i++) spawnEnemy(s, 'wolf', 1, false, 0, p.x + ((i % 3) - 1) * 700, p.y + r.sinkR + 300, r.wolfHp);
  cue(`sunk${w.falls}`);
};

export const lagoonTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = lr(s);
  const st = s.senda;
  if (!r || !st) return;
  const is = (st.isla ??= {});
  const w = (is.lagoon ??= fresh(r));
  if (!s.started || w.done >= 0) return;
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (h) {
    // A crossing read up close: the hero still by it a moment (the frost brushed off the ice).
    const by = h.moving ? -1 : r.crossings.findIndex((c, i) => w.seen[i] === 0 && withinRange(h.x, h.y, c.at.x, c.at.y, r.see));
    if (by !== w.reading) {
      w.reading = by;
      w.readAt = s.tick;
    } else if (by >= 0 && s.tick - w.readAt >= r.readT) {
      const c = r.crossings[by] as { at: Pt };
      w.seen[by] = 1;
      w.reading = -1;
      ev(s, { t: 'senda', kind: 'iSeen', npc: '', x: c.at.x, y: c.at.y, n: by });
      if (lagoonHers(r, by)) {
        const n = w.seen.filter((v, j) => v === 1 && lagoonHers(r, j)).length;
        cue(`seen${n}`);
      }
    }
    // Breaking the ice where it was sent: standing there a moment.
    if (w.goal >= 0) {
      const p = r.ends[w.goal] as Pt;
      if (withinRange(h.x, h.y, p.x, p.y, r.reach) && !h.moving) {
        if (w.t0 < 0) w.t0 = s.tick;
        h.facing = 0;
        h.anim = 'attack';
        if (s.tick - w.t0 >= r.breakT) breakAt(s, r, w, h, w.goal, cue);
      } else if (w.t0 >= 0 && !withinRange(h.x, h.y, p.x, p.y, r.reach)) w.t0 = -1;
    }
  }
  // Wolves off the shore.
  if (s.waveT >= w.next) {
    w.next = s.waveT + r.wolves.every;
    const p = r.shore[rngInt(s.rng, r.shore.length)] as Pt;
    const n = rngRange(s.rng, r.wolves.n[0], r.wolves.n[1]);
    for (let i = 0; i < n; i++) spawnEnemy(s, 'wolf', 1, false, 0, p.x + ((i % 3) - 1) * 450, p.y + (i >> 1) * 400, r.wolfHp);
    cue(`pack${s.waveT >= r.wolves.first + r.wolves.every ? 2 : 1}`);
  }
};

/** Another order of the hero's own: the break it was sent to is off. */
export const lagoonOff = (s: BattleState): void => {
  const w = ls(s);
  if (w) {
    w.goal = -1;
    w.t0 = -1;
  }
};

/** Her crossings read, of all of them. */
export const lagoonGoal = (s: BattleState): { done: number; need: number } => {
  const r = lr(s);
  const w = ls(s);
  if (!r) return { done: 0, need: 1 };
  const hers = r.crossings.map((_, i) => i).filter((i) => lagoonHers(r, i));
  return { done: w ? hers.filter((i) => w.seen[i] === 1).length : 0, need: hers.length };
};

export const lagoonResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = ls(s);
  return w && w.done >= 0 && s.tick >= w.done + 36 ? 'won' : null;
};
